// src/hooks/useModalKeyboardHandlers.js
import { useEffect } from 'react';

/**
 * Custom hook for handling keyboard events in modals
 * Manages ESC and Enter key interactions with proper modal priority
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {boolean} loading - Whether the modal is in a loading state
 * @param {Function} onClose - Callback to close the modal
 * @param {Object} options - Configuration options
 * @param {boolean} options.showOrgUsersModal - Whether org users modal is open (higher priority)
 * @param {Object} options.deleteConfirmation - Delete confirmation state
 * @param {Object} options.deauthorizeConfirmation - Deauthorize confirmation state
 * @param {boolean} options.showCreateForm - Whether create form is showing
 * @param {Object} options.editingOrganization - Organization being edited
 * @param {Function} options.onCancelForm - Callback to cancel form
 * @param {Function} options.onClearDeleteConfirmation - Callback to clear delete confirmation
 * @param {Function} options.onClearDeauthorizeConfirmation - Callback to clear deauth confirmation
 */
export const useEscapeKey = (isOpen, loading, onClose, options = {}) => {
  const {
    showOrgUsersModal,
    deleteConfirmation,
    deauthorizeConfirmation,
    showCreateForm,
    editingOrganization,
    onCancelForm,
    onClearDeleteConfirmation,
    onClearDeauthorizeConfirmation,
  } = options;

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !loading) {
        // If OrgUsersModal is open, don't handle ESC (let it handle its own closing)
        if (showOrgUsersModal) {
          return;
        }

        // If delete confirmation is open, close it instead of the main modal
        if (deleteConfirmation && onClearDeleteConfirmation) {
          onClearDeleteConfirmation();
        } else if (deauthorizeConfirmation && onClearDeauthorizeConfirmation) {
          onClearDeauthorizeConfirmation();
        } else if ((showCreateForm || editingOrganization) && onCancelForm) {
          // If create/edit form is showing, go back to list instead of closing modal
          onCancelForm();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [
    isOpen,
    loading,
    onClose,
    showOrgUsersModal,
    deleteConfirmation,
    deauthorizeConfirmation,
    showCreateForm,
    editingOrganization,
    onCancelForm,
    onClearDeleteConfirmation,
    onClearDeauthorizeConfirmation,
  ]);
};

/**
 * Hook for handling Enter key in result modals
 * Closes the modal when Enter is pressed and a result is shown
 * 
 * @param {Object} modalState - Modal state object
 * @param {boolean} modalState.isOpen - Whether modal is open
 * @param {Object} modalState.result - Result object (if present, modal can be closed)
 * @param {boolean} modalState.isLoading - Whether modal is loading
 * @param {Function} setModalState - Function to update modal state
 */
export const useEnterKeyForModal = (modalState, setModalState) => {
  useEffect(() => {
    const handleEnterKey = (event) => {
      if (
        event.key === 'Enter' &&
        modalState.isOpen &&
        modalState.result &&
        !modalState.isLoading
      ) {
        event.preventDefault();
        setModalState({
          isOpen: false,
          isLoading: false,
          result: null,
        });
      }
    };

    if (modalState.isOpen && modalState.result) {
      document.addEventListener('keydown', handleEnterKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [modalState.isOpen, modalState.result, modalState.isLoading, setModalState]);
};
